import React, {useEffect, useRef, useState} from 'react';
import {Button} from "primereact/button";
import {useDispatch, useSelector} from "react-redux";
import {useLocation, useNavigate} from "react-router-dom";
import {useTranslate} from "../useTranslate";
import {Avatar as PrimeAvatar} from "primereact/avatar";
import {OverlayPanel} from "primereact/overlaypanel";
import {logout} from "../../auth/auth-slice";
import {Dropdown} from "primereact/dropdown";
import {public_url, timeSince, timestampToDate} from "../utils";
import Flag from "react-flagpack";
import 'react-flagpack/dist/style.css'
import {getSiteConfigs, setSiteConfigs} from "../storage";
import {fetchAppData} from "../app-slice";
import {MultiStateCheckbox} from "primereact/multistatecheckbox";
import LanguageItem from "./language-item";
import {ListBox} from "primereact/listbox";
import {Badge} from "primereact/badge";
import Avatar from "./avatar";
import Icon from "./icon";
import {useRealtime} from "../realtime/useRealtime";
import useUserEventLog from "../../models/user-event-log/use-user-event-log";

const notificationKey = (notification) => notification?.documentId ?? notification?.id;

function Toolbar({setVisibleSidebar, visibleSidebar, onTitleClicked, readOnly = false}) {
    const {config, pages, homePageIndex, workspaceMembershipData, notifications} = useSelector((state) => state.app);
    const {user} = useSelector((state) => state.auth);
    const dispatch = useDispatch();
    const location = useLocation();
    const translate = useTranslate();
    const navigate = useNavigate();
    const [currentPageIndex, setCurrentPageIndex] = useState(homePageIndex);
    const [showHomePageButton, setShowHomePageButton] = useState(false);
    const [wsConnected, setWsConnected] = useState(false);
    const [readingNotificationKey, setReadingNotificationKey] = useState(null);
    const userRef = useRef();
    const notificationRef = useRef();
    const darkLightOptions = [{value:"dark", icon:"pi pi-moon"},{value:"light", icon:"pi pi-sun"}];
    const realTime = useRealtime();
    const userEventLog = useUserEventLog();
    const unreadNotifications = (notifications || []).filter((notification) => (
        !notification.isRead && notification.displayType !== "invisible"
    ));

    const markNotificationRead = async (notification) => {
        if (readOnly) return;
        const key = notificationKey(notification);
        if (!key || readingNotificationKey) return;

        setReadingNotificationKey(key);
        try {
            await userEventLog.updateUserEventLog(key, {isRead: true});
        } catch (error) {
            console.error("[Toolbar mark notification read]", error);
        } finally {
            setReadingNotificationKey(null);
        }
    };

    useEffect(()=>{

        const currentPageIndex = location.pathname==='/'?homePageIndex:pages.findIndex(e=>location.pathname===e['nav-url']);
        setCurrentPageIndex(currentPageIndex);
        setShowHomePageButton(currentPageIndex!==homePageIndex);
    },[location.pathname])

    return (
        <div className={"w-full surface-ground border-bottom-1 border-200 flex align-items-center p-2 justify-content-between h-4rem"}>
            <div className="flex align-items-center gap-2">
                {realTime.isConnected&&(<Icon icon="mi mi:cloud_sync"/>)}
                {config.sidebar?(<span className="pi pi-bars cursor-pointer" onClick={()=>{setVisibleSidebar(!visibleSidebar)}}/>):(showHomePageButton&&<span className="pi pi-chevron-right cursor-pointer" onClick={()=>{navigate("/")}}/>)}
                {/*<span className="yekan-bakh-font">{`${currentPageIndex>0?translate(pages[currentPageIndex].name):""}`}</span>*/}
                <div className="flex align-items-center gap-2 cursor-pointer" onClick={(e)=>{onTitleClicked?.(e)}}>
                    <span className="font-bold">{`${workspaceMembershipData?.workspace?.name??""}`}</span>
                    <i className="pi pi-cog text-xs text-500"/>
                </div>
            </div>
            <div className="flex align-items-center gap-2">
                {config['dark-mode-switch']&&(
                    <div className="border-1 p-2 border-100 border-round flex gap-1 align-items-center ml-2">
                        <MultiStateCheckbox empty={false} value={config["dark-mode"]?"dark":"light"} defaultValue="light" onChange={(e) => {setSiteConfigs({...getSiteConfigs(), "dark-mode":e.value==="dark"}); dispatch(fetchAppData())}} options={darkLightOptions} optionValue="value" />
                        <span className="text-xs">{config["dark-mode"]?translate("dark"):translate("light")}</span>
                    </div>
                )}
                {config["multi-language"]&&<Dropdown panelClassName={`${config.dir}`} options={config['support-languages']} value={config['support-languages'][config['support-languages'].findIndex(l=>l.code===(getSiteConfigs()&&getSiteConfigs().language?getSiteConfigs().language.code:config.language))]} valueTemplate={(option, props)=>(<LanguageItem option={option} props={props} />)} itemTemplate={(option, props)=>(<LanguageItem option={option} props={props} />)} onChange={(e)=>{setSiteConfigs({...getSiteConfigs(), language:e.value}); dispatch(fetchAppData())}}/>}
                <i className="text-2xl pi pi-bell bg-transparent p-overlay-badge pl-2 cursor-pointer" onClick={(e) => notificationRef.current.toggle(e)}>
                    {!!unreadNotifications.length&&<Badge severity="danger" />}
                </i>
                <OverlayPanel
                    className="w-20rem rtl"
                    ref={notificationRef}
                    pt={{content:()=>({className:"p-0 overflow-auto", style:{maxHeight:"24rem"}})}}
                >
                    {unreadNotifications.length ? (
                        <ListBox
                            className="border-none"
                            options={unreadNotifications.map((notification) => ({
                                label: translate(notification.title),
                                value: notificationKey(notification),
                                className: "p-0 border-round",
                                data: notification,
                            }))}
                            itemTemplate={(option) => (
                                <ItemTemplate
                                    option={option}
                                    pending={readingNotificationKey === notificationKey(option.data)}
                                    readOnly={readOnly}
                                    onMarkRead={markNotificationRead}
                                />
                            )}
                        />
                    ) : (
                        <div className="text-xs text-500 text-center p-4">اعلان خوانده‌نشده‌ای ندارید</div>
                    )}
                </OverlayPanel>

                {/*<PrimeAvatar icon={!user?.name&&"pi pi-user"} label={user?.name?.charAt(0).toUpperCase()} shape="circle" size="large" className="ml-2" onClick={(e) => userRef.current.toggle(e)}/>*/}
                <Avatar size="large" className="text-sm font-bold" memberData={user} onClick={(e) => userRef.current.toggle(e)}/>
                <OverlayPanel className="w-7rem" ref={userRef} pt={{content:()=>({className:"p-0"})}}>
                    <div className="flex flex-column gap-1">
                        {config['user-profile']&&(<Button className="p-2 text-xs p-button-text border-round-sm shadow-none" label={translate('user-account')} onClick={()=>{navigate('/user-account'); userRef.current.hide();}} />)}
                        <Button className="p-2 text-xs p-button-text border-round-sm shadow-none" label={translate('sign-out')} onClick={()=>{dispatch(logout())}}/>
                    </div>
                </OverlayPanel>


            </div>


        </div>
    );
}

function ItemTemplate({option, pending, readOnly, onMarkRead}) {
    return(
        <div className="flex align-items-center justify-content-between gap-2 border-round p-2 pr-2">
            <div className="flex gap-2 align-items-center min-w-0">
                <i className="pi pi-circle-fill text-primary-300 text-xs"/>
                <div className="flex flex-column min-w-0">
                    <span className="text-xs">{option.label}</span>
                    <span className="text-xs text-300">{timeSince(new Date(option.data.createdAt)/1000)}</span>
                </div>
            </div>
            <Button
                label="خوانده شد"
                icon="pi pi-check"
                text
                size="small"
                className="text-xs gap-1 flex-shrink-0"
                loading={pending}
                disabled={pending || readOnly}
                onClick={(event) => {
                    event.stopPropagation();
                    onMarkRead(option.data);
                }}
            />
        </div>
    )
}

export default Toolbar;
